import { useState, type FormEvent } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import type { LeadPayload } from "@/types/property";
import { cn } from "@/lib/utils";

interface LeadFormProps {
  source: LeadPayload["source"];
  propertyId?: string;
  propertyReference?: string;
  defaultMessage?: string;
  extraFields?: {
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
  }[];
  className?: string;
  cta?: string;
  successMessage?: string;
  /**
   * When true (default for the main contact forms), use the May 2026
   * brief's field set: Full Name + Email + Phone + "I am looking to…"
   * dropdown.
   * When false, use the legacy compact set with a single name field
   * and free-form message — used by the property detail sidebar and
   * the list-property page where the simpler form is more appropriate.
   */
  useFullContactFields?: boolean;
  /**
   * Optional preselected value for the intent dropdown.
   */
  defaultIntent?: IntentValue;
  /**
   * Label shown above the compact free-form textarea.
   */
  messageLabel?: string;
}

type IntentValue =
  | "buy"
  | "rent"
  | "sell"
  | "let"
  | "off-plan"
  | "valuation"
  | "";

const INTENT_OPTIONS: { value: IntentValue; label: string }[] = [
  { value: "buy", label: "Buy a property" },
  { value: "rent", label: "Rent a property" },
  { value: "sell", label: "Sell my property" },
  { value: "let", label: "Let my property" },
  { value: "off-plan", label: "Invest in off-plan" },
  { value: "valuation", label: "Get a market valuation" },
];

export function LeadForm({
  source,
  propertyId,
  propertyReference,
  defaultMessage,
  extraFields = [],
  className,
  cta = "Send Enquiry",
  successMessage = "Thank you — one of our advisors will be in touch within 24 hours.",
  useFullContactFields = true,
  defaultIntent = "",
  messageLabel = "Anything we should know?",
}: LeadFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string>();
  const [intent, setIntent] = useState<IntentValue>(defaultIntent);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage(undefined);

    const formData = new FormData(e.currentTarget);

    const name = useFullContactFields
      ? [
          String(formData.get("firstName") ?? "").trim(),
          String(formData.get("lastName") ?? "").trim(),
        ]
          .filter(Boolean)
          .join(" ")
      : String(formData.get("name") ?? "").trim();

    const meta: Record<string, string> = {};
    if (useFullContactFields) {
      if (intent) meta.intent = intent;
    }
    for (const f of extraFields) {
      const v = formData.get(f.name);
      if (v) meta[f.name] = String(v);
    }

    try {
      await api.createLead({
        name,
        email: String(formData.get("email") ?? "").trim() || undefined,
        phone: String(formData.get("phone") ?? "").trim(),
        message: String(formData.get("message") ?? "").trim() || undefined,
        source,
        propertyId,
        propertyReference,
        meta,
      });
      setStatus("ok");
      e.currentTarget.reset();
      setIntent(defaultIntent);
    } catch (err) {
      setStatus("error");
      setErrorMessage(String(err));
    }
  };

  if (status === "ok") {
    return (
      <div
        className={cn(
          "border border-[var(--line-strong)] bg-[var(--surface)] px-6 py-12 text-center",
          className,
        )}
      >
        <p className="eyebrow">Thanks!</p>
        <p
          className="mt-4 font-display text-[28px] text-[var(--text)] sm:text-[32px]"
          style={{ fontVariationSettings: '"opsz" 48' }}
        >
          Message Received.
        </p>
        <p className="mt-3 max-w-sm mx-auto text-[14px] text-[var(--text-soft)]">
          {successMessage}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={cn("flex flex-col gap-6", className)}>
      {useFullContactFields ? (
        <div className="grid gap-6 md:grid-cols-2">
          <Field
            id="firstName"
            name="firstName"
            label="First Name"
            autoComplete="given-name"
            required
          />
          <Field
            id="lastName"
            name="lastName"
            label="Last Name"
            autoComplete="family-name"
            required
          />
        </div>
      ) : (
        <Field
          id="name"
          name="name"
          label="Your Name"
          autoComplete="name"
          required
        />
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Field
          id="email"
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          required
        />
        <Field
          id="phone"
          name="phone"
          type="tel"
          label="Mobile / WhatsApp"
          autoComplete="tel"
          required
        />
      </div>

      {useFullContactFields && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="intent" className="sr-only">
            I am looking to…
          </Label>
          <Select value={intent} onValueChange={(v) => setIntent(v as IntentValue)}>
            <SelectTrigger
              id="intent"
              className="h-12 w-full rounded-none border border-[var(--line-strong)] bg-[var(--surface)] px-3.5 text-[15px] text-[var(--text)] shadow-[0_10px_28px_-26px_rgba(20,30,48,0.42)] transition-colors hover:border-[var(--gold-bright)] focus-visible:border-[var(--gold-bright)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-bright)] focus-visible:ring-0 data-[size=default]:h-12"
            >
              <SelectValue placeholder="I am looking to…" />
            </SelectTrigger>
            <SelectContent>
              {INTENT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {extraFields.map((f) => (
        <Field
          key={f.name}
          id={f.name}
          name={f.name}
          label={f.label}
          placeholder={f.placeholder}
          required={f.required}
        />
      ))}

      <div className="flex flex-col gap-2">
        <Label htmlFor="message" className="sr-only">
          {useFullContactFields ? "Tell us about your requirements" : messageLabel}
        </Label>
        <Textarea
          id="message"
          name="message"
          defaultValue={defaultMessage}
          placeholder={
            useFullContactFields
              ? "Tell us about your requirements — area, budget, timeline…"
              : messageLabel
          }
          className="min-h-[140px] rounded-none border border-[var(--line-strong)] bg-[var(--surface)] px-3.5 py-3 text-[15px] text-[var(--text)] placeholder:text-[var(--text-muted)] shadow-[0_10px_28px_-26px_rgba(20,30,48,0.42)] transition-colors hover:border-[var(--gold-bright)] focus-visible:border-[var(--gold-bright)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-bright)] focus-visible:ring-0"
        />
      </div>

      <button
        type="submit"
        disabled={status === "loading"}
        className="btn btn-primary mt-3 disabled:opacity-50"
      >
        {status === "loading" && <Loader2 className="size-4 animate-spin" />}
        {cta}
        <ArrowRight className="size-3.5" />
      </button>

      {status === "error" && (
        <p className="text-[13px] text-red-400">
          {errorMessage ?? "Something went wrong. Please try again."}
        </p>
      )}

      <p className="text-[12px] leading-relaxed text-[var(--text-muted)]">
        Your details stay with our advisors only. We never share them with
        anyone else.
      </p>
    </form>
  );
}

/**
 * Per the client's feedback the field titles live inside the boxes as
 * placeholders ("First Name", "Last Name", "Email…") with no visible
 * labels; a sr-only label keeps each input accessibly named.
 */
function Field({
  id,
  name,
  label,
  type = "text",
  required,
  placeholder,
  autoComplete,
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className="sr-only">
        {label}
      </Label>
      <Input
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={`${placeholder ?? label}${required ? " *" : ""}`}
        autoComplete={autoComplete}
        className="h-12 rounded-none border border-[var(--line-strong)] bg-[var(--surface)] px-3.5 text-[15px] text-[var(--text)] placeholder:text-[var(--text-muted)] shadow-[0_10px_28px_-26px_rgba(20,30,48,0.42)] transition-colors hover:border-[var(--gold-bright)] focus-visible:border-[var(--gold-bright)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-bright)] focus-visible:ring-0"
      />
    </div>
  );
}
